<template>
  <div class="container">
    <div class="legend">
      <el-checkbox-group class="mobile_check" v-model="checkedLegends" @change="handleLegendChange">
        <template v-for="(item, index) in top16Legend">
          <el-checkbox :key="index" :label="item"> <span class="ric" :style="{ background: top16Colors[index] }"></span>{{ item }} </el-checkbox>
          <br :key="`br-${index}`" v-if="index > 0 && index % 4 === 0" />
        </template>
      </el-checkbox-group>
    </div>
    <div class="chart-scroll-container">
      <div ref="LineChartShort" class="LineChartShort"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "LineChartLong", // 组件名保持为 LineChartLong
  props: {
    chartData: {
      type: Array,
      default: () => [],
    },
    alarmTotal: {
      type: Number,
      default: 0,
    },
    colorStops: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      chartInstance: null,
      /* 原始数据保持不动，仅用于计算 */
      rawLegendData: ["Human Experts", "Gemini-1.5-Pro-002", "Gemini-1.5-Flash-002", "Gemini-1.5-Flash-8B", "GPT-4o", "Qwen-VL-Max"],
      rawSeriesData: [
        [81.33, 85.33, 90.67, 82.61, 85.51, 91.16, 81.97],
        [51.91, 60.85, 73.45, 67.69, 72.05, 82.18, 59.8],
        [40.43, 51.49, 66.0, 54.15, 65.5, 75.49, 47.29],
        [25.53, 34.04, 47.63, 39.3, 44.54, 60.97, 32.42],
        [2.83, 7.29, 23.2, 2.9, 7.88, 26.74, 2.87],
        [0.94, 1.88, 4.83, 0.95, 3.32, 7.12, 0.95],
      ],
      /* 仅保留 16 种颜色 */
      rawColors: [
        "#FF5F5F",
        "#9E7CFF",
        "#40C4FF",
        "#69F0AE",
        "#FFF153",
        "#FF764B",
        "#EE88FF",
        "#279BCF",
        "#9CCC65",
        "#AEE5FF",
        "#FFA8B3",
        "#FFD700",
        "#FF69B4",
        "#8A2BE2",
        "#FF4500",
        "#2E8B57",
      ],
      checkedLegends: [],
    };
  },
  computed: {
    /* 1. 按 Avg 降序取前 16 个模型 */
    sortedTop16() {
      return this.rawSeriesData
        .map((d, idx) => ({ idx, avg: d[6] }))
        .sort((a, b) => b.avg - a.avg)
        .slice(0, 16);
    },
    /* 2. 仅 16 种颜色，且与排序后顺序一一对应 */
    top16Colors() {
      const base16 = this.rawColors.slice(0, 16);
      return this.sortedTop16.map((_, i) => base16[i]);
    },
    top16Legend() {
      return this.sortedTop16.map((item) => this.rawLegendData[item.idx]);
    },
    top16Series() {
      return this.sortedTop16.map((item) => this.rawSeriesData[item.idx]);
    },
    /* 3. 构造 modelColors 映射（排序后模型名 -> 上面 16 色） */
    modelColors() {
      const map = {};
      this.top16Legend.forEach((name, i) => {
        map[name] = this.top16Colors[i];
      });
      return map;
    },
  },
  watch: {
    chartData: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.updateChart();
        });
      },
    },
    // 新增：监听图表容器宽度变化
    "$refs.LineChartShort.offsetWidth": {
      handler() {
        this.resizeChart();
      },
      immediate: false,
    },
  },
  mounted() {
    this.initChart();
    this.checkedLegends = [...this.top16Legend];
    this.updateChart();
    // 新增：窗口resize监听
    window.addEventListener("resize", this.resizeChart);
  },
  beforeDestroy() {
    // 新增：移除监听，避免内存泄漏
    window.removeEventListener("resize", this.resizeChart);
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
  },
  methods: {
    initChart() {
      this.chartInstance = this.$echarts.init(this.$refs.LineChartShort);
    },
    updateChart() {
      const option = this.chartOption();
      this.chartInstance.setOption(option, true);
    },
    // 新增：图表尺寸调整方法
    resizeChart() {
      if (this.chartInstance) {
        this.chartInstance.resize();
      }
    },
    chartOption() {
      const visibleSeries = this.top16Legend.reduce((acc, name) => {
        if (this.checkedLegends.includes(name)) {
          const dataIdx = this.top16Legend.indexOf(name);
          acc.push({
            name,
            type: "bar",
            data: this.top16Series[dataIdx],
            color: this.modelColors[name],
            barWidth: 6,
            barGap: 0.2,
          });
        }
        return acc;
      }, []);

      return {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          textStyle: {
            lineHeight: 18,
            fontSize: 12, // 新增：适配小屏幕文字
          },
          confine: true,
          padding: 8, // 新增：减少内边距
        },
        legend: { show: false },
        grid: {
          left: "13%",
          right: "3%",
          bottom: "50px", // 增加底部间距，避免x轴标签被遮挡
          top: "5%",
          containLabel: false,
        },
        xAxis: [
          {
            type: "category",
            data: [
              "Object Recall@1x, tIoU=0.7",
              "Object Recall@1x, tIoU=0.5",
              "Object Average mAP",
              "Scene Recall@1x, tIoU=0.7",
              "Scene Recall@1x, tIoU=0.5",
              "Scene Average mAP",
              "Avg Recall@1x, tIoU=0.7",
            ],
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
              show: true,
              textStyle: { color: "#E0E0E0", fontSize: 11 }, // 新增：缩小文字
              interval: 0,
              rotate: 15, // 新增：标签旋转
            },
          },
        ],
        yAxis: [
          {
            type: "value",
            axisLabel: {
              formatter: "{value}",
              textStyle: { color: "#E0E0E0", fontSize: 11 }, // 新增：缩小文字
            },
            axisTick: { show: false },
            axisLine: {
              show: false,
              lineStyle: { color: "#E0E0E0", width: 1, type: "solid" },
            },
            splitLine: { lineStyle: { color: "#7A7A7A" } },
          },
        ],
        series: visibleSeries,
      };
    },
    handleLegendChange() {
      this.updateChart();
    },
  },
};
</script>

<style lang="scss" scoped>
// 仅保留平板和移动端断点
$tablet: 1024px; // 平板及以下（≤1024px）
$mobile: 768px; // 移动端及以下（≤768px）

.container {
  width: 100%;
  height: 100%;
  min-height: 500px; // 新增：保证最小高度
  display: flex;
  flex-direction: column;
  padding: 10px 0; // 新增：上下内边距
}

.legend {
  width: 100%;
  flex-shrink: 0;
  padding: 0 15px; // 新增：左右内边距
  margin-bottom: 15px; // 新增：与图表间距
  .ric {
    width: 12px;
    height: 12px;
    border-radius: 6px;
    margin-right: 4px;
    opacity: 0.6;
  }
}

.chart-scroll-container {
  flex: 1;
  width: 100%;
  overflow-x: auto; // 开启横向滚动
  overflow-y: hidden; // 隐藏纵向滚动
  -webkit-overflow-scrolling: touch; // 优化移动端滚动体验（平滑滚动）
  padding-bottom: 10px; // 预留滚动条空间，避免遮挡图表
}

.LineChartShort {
  flex: 1;
  width: 100%;
  min-height: 500px; // 新增：图表最小高度
  min-width: 500px;
}

// 复选框组基础样式
::v-deep {
  .el-checkbox-group.mobile_check {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 0; // 新增：垂直间距
    .el-checkbox {
      display: flex;
      align-items: center;
      width: 290px; // 默认宽度
      margin-right: 0;
      margin-bottom: 0;
      font-size: 14px; // 基础字体
      &.is-checked {
        .ric {
          opacity: 1;
        }
      }
      .el-checkbox__input {
        margin-top: 1px;
        margin-right: 6px; // 新增：复选框与文字间距
      }
      .el-checkbox__input.is-checked .el-checkbox__inner {
        background-color: #ffffff;
        border-color: #ffffff;
      }
      .el-checkbox__inner {
        border: 1px solid #ffffff;
        background-color: transparent;
        width: 14px; // 基础尺寸
        height: 14px;
      }
      .el-checkbox__inner::after {
        box-sizing: content-box;
        content: "";
        border: 1px solid #000;
        border-left: 0;
        border-top: 0;
        height: 7px;
        left: 4px;
        position: absolute;
        top: 1px;
        transform: rotate(45deg) scaleY(0);
        width: 3px;
        transition: transform 0.15s ease-in 0.05s;
        transform-origin: center;
      }
      .el-checkbox__input.is-checked .el-checkbox__inner::after {
        transform: rotate(45deg) scaleY(1);
      }
      .el-checkbox__input.is-checked + .el-checkbox__label {
        color: #e0e0e0;
      }
      .el-checkbox__label {
        display: flex;
        align-items: center;
        color: #e0e0e0;
      }
    }
  }
}

// 平板适配（≤1024px）
@media (max-width: $tablet) {
  .legend {
    padding: 0 10px;
  }
  ::v-deep {
    .el-checkbox-group.mobile_check {
      .el-checkbox {
        width: 33%; // 一行3个
        font-size: 13px; // 缩小字体
      }
    }
  }
  .LineChartShort {
    min-height: 500px;
  }
  // 平板x轴标签优化
  ::v-deep .ec-x-axis text {
    transform: rotate(20deg);
    text-anchor: start !important;
  }
}

// 移动端适配（≤768px）
@media (max-width: $mobile) {
  .container {
    min-height: 500px;
  }
  .legend {
    padding: 0 8px;
    margin-bottom: 10px;
  }
  ::v-deep {
    .el-checkbox-group.mobile_check {
      gap: 8px 0;
      .el-checkbox {
        width: 50%; // 一行2个
        font-size: 12px; // 进一步缩小字体
      }
      .el-checkbox__inner {
        width: 13px;
        height: 13px;
      }
      .el-checkbox__inner::after {
        height: 6px;
        width: 2.5px;
        left: 3.5px;
      }
    }
  }
  .LineChartShort {
    min-height: 500px;
  }
  // 移动端坐标轴文字优化
  ::v-deep .ec-x-axis text {
    font-size: 10px !important;
    transform: rotate(30deg);
    text-anchor: start !important;
  }
  ::v-deep .ec-y-axis text {
    font-size: 10px !important;
  }
  // 移动端tooltip优化
  ::v-deep .ec-tooltip {
    font-size: 11px !important;
  }
}
</style>
